import {
  Select,
  createListCollection,
  Portal,
  Show,
  Flex,
  useSelectContext,
  HStack,
  InputGroup,
  Input,
  Stack,
  Text,
} from '@chakra-ui/react';
import { isEqual } from 'lodash';
import { GenderEnum } from '@halo/chat-sdk-react';
import { ClearIcon, SelectIcon, ManIcon, WomanIcon, OtherGenderIcon } from '@halo/ui-chat-react';
import type { FieldProps } from './helpers';

type EditGenderProps = Pick<FieldProps, 'user'> & {
  onChange: (item: { gender: string; gender_name?: string }) => void;
};

const EditGender = ({ user, onChange }: EditGenderProps) => {
  const { t } = useTranslation();
  const [gender, setGender] = useState<string[]>();
  const [isClickOther, setIsClickOther] = useState<boolean>(false);
  const [otherName, setOtherName] = useState<string>('');
  const collection = createListCollection({
    items: [
      { value: GenderEnum.MALE, label: t('profile.male'), icon: <ManIcon /> },
      { value: GenderEnum.FEMALE, label: t('profile.female'), icon: <WomanIcon /> },
      { value: GenderEnum.OTHER, label: t('profile.other'), icon: <OtherGenderIcon /> },
    ],
  });

  useEffect(() => {
    if (isEqual(user.gender, undefined)) {
      return;
    }
    switch (user.gender) {
      case GenderEnum.FEMALE:
      case GenderEnum.MALE:
      case GenderEnum.OTHER: {
        setGender([user.gender]);
        break;
      }
      default: {
        setGender([GenderEnum.OTHER]);
        setOtherName(user.gender || '');
      }
    }
  }, [user.gender]);
  return (
    <Flex w="full" alignItems="center" justifyContent="space-between">
      <Show when={!isClickOther}>
        <Select.Root
          collection={collection}
          size="sm"
          w="max-content"
          value={gender}
          onValueChange={({ value }) => {
            setOtherName('');
            setGender(value);
            onChange({
              gender: value[0],
            });
          }}
          css={{
            '&:hover .trigger': {
              borderColor: 'border',
            },
          }}
        >
          <Select.HiddenSelect />
          <Select.Control>
            <Select.Trigger
              cursor="pointer"
              border="1px solid transparent"
              h="2.285em"
              minH="2.285em"
              className="trigger"
            >
              <SelectValue placeholder={t('profile.selectGender')} othername={otherName} />
            </Select.Trigger>
            <Select.IndicatorGroup>
              <Select.Indicator />
            </Select.IndicatorGroup>
          </Select.Control>
          <Portal disabled>
            <Select.Positioner>
              <Select.Content w="100px">
                {collection.items.map((gender) => {
                  return (
                    <Select.Item
                      item={gender}
                      key={gender.value}
                      cursor="pointer"
                      color="fg"
                      fontSize="xs"
                      fontWeight="medium"
                      bg="transparent"
                      borderTop={
                        gender.value === GenderEnum.OTHER && otherName
                          ? '1px solid var(--colors-border-subtle)'
                          : '0px'
                      }
                    >
                      {gender.value !== GenderEnum.OTHER && (
                        <>
                          {gender.label}
                          <Select.ItemIndicator>
                            <SelectIcon
                              style={{
                                width: 10,
                              }}
                            />
                          </Select.ItemIndicator>
                        </>
                      )}
                      {gender.value === GenderEnum.OTHER && (
                        <Stack w="full">
                          <HStack
                            w="full"
                            justify="space-between"
                            onClick={() => {
                              if (gender.value === GenderEnum.OTHER) {
                                setGender([GenderEnum.OTHER]);
                                setIsClickOther(true);
                              }
                            }}
                          >
                            {gender.label}
                            {!otherName && (
                              <Select.ItemIndicator>
                                <SelectIcon
                                  style={{
                                    width: 10,
                                  }}
                                />
                              </Select.ItemIndicator>
                            )}
                          </HStack>
                          {otherName && (
                            <HStack w="full" justify="space-between" cursor="default">
                              <Text maxW="80%" truncate>
                                {otherName}
                              </Text>
                              <Select.ItemIndicator>
                                <SelectIcon
                                  style={{
                                    width: 10,
                                  }}
                                />
                              </Select.ItemIndicator>
                            </HStack>
                          )}
                        </Stack>
                      )}
                    </Select.Item>
                  );
                })}
              </Select.Content>
            </Select.Positioner>
          </Portal>
        </Select.Root>
      </Show>

      <Show when={isClickOther}>
        <ClearInput
          value={otherName}
          onChange={(value) => {
            onChange({
              gender: value,
            });
            setOtherName(value);
            setIsClickOther(false);
          }}
          onClear={() => {
            setIsClickOther(false);
          }}
        />
      </Show>
    </Flex>
  );
};
const SelectValue = ({
  othername,
  placeholder,
}: {
  othername?: string;
  placeholder?: string;
}) => {
  const select = useSelectContext();
  const items = select.selectedItems as Array<{
    value: string;
    label: ReactNode | string;
    icon: ReactNode;
  }>;
  const { label, icon } = items[0] || {};
  return (
    <Select.ValueText maxWidth="none">
      <Flex fontSize="xs" color={label ? 'fg' : 'var(--colors-fg-subtle)'} pr="5">
        {label ? (
          <HStack minW="40px" justify="flex-end" gap="1">
            {icon}
            {othername ? othername : label}
          </HStack>
        ) : (
          placeholder
        )}
      </Flex>
    </Select.ValueText>
  );
};
interface ClearInputProps {
  onChange: (value: string) => void;
  value: string;
  onClear: () => void;
}
const ClearInput = (props: ClearInputProps) => {
  const { t } = useTranslation();
  const { value, onChange, onClear } = props;
  const [text, setText] = useState('');
  useEffect(() => {
    setText(value);
  }, [value]);
  const getCharCount = (str: string) => {
    if (typeof Intl?.Segmenter === 'function') {
      return [...new Intl.Segmenter().segment(str)].length;
    }
    return [...str].length;
  };

  const truncateToMax = (str: string, max: number) => {
    const chars = [...str];
    return chars.length > max ? chars.slice(0, max).join('') : str;
  };
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    const charCount = getCharCount(newValue);
    if (charCount <= 12) {
      setText(newValue);
    } else {
      const truncated = truncateToMax(newValue, 12);
      setText(truncated);
    }
  }, []);
  return (
    <InputGroup
      endElement={
        <ClearIcon
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onClear();
          }}
        />
      }
    >
      <Input
        placeholder={t('profile.enterGender')}
        w="150px"
        borderColor="border.muted"
        color="fg.muted"
        size="xs"
        value={text}
        onChange={handleInputChange}
        onBlur={() => {
          onChange(text);
        }}
        onKeyUp={(e) => {
          if (e.key.toLowerCase() == 'enter') {
            onChange(text);
          }
        }}
        autoFocus
      />
    </InputGroup>
  );
};

export { EditGender };
